import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./ExpenseReport.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function monthStartKey() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function csvValue(value) {
  const text = String(value ?? "").replaceAll('"', '""');
  return `"${text}"`;
}

export default function ExpenseReport() {
  const [shop, setShop] = useState("ALL");
  const [dateFrom, setDateFrom] = useState(monthStartKey());
  const [dateTo, setDateTo] = useState(todayKey());
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [detailRows, setDetailRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadReport(categoryValue = selectedCategory) {
    try {
      setLoading(true);
      setError("");

      const summaryUrl =
        `${API_BASE}/api/erp/expense-summary?shop=${shop}` +
        `&dateFrom=${dateFrom}&dateTo=${dateTo}`;

      const detailUrl =
        `${API_BASE}/api/erp/expense-details?shop=${shop}` +
        `&dateFrom=${dateFrom}&dateTo=${dateTo}` +
        `&category=${encodeURIComponent(categoryValue)}`;

      const [summaryRes, detailRes] = await Promise.all([
        fetch(summaryUrl),
        fetch(detailUrl),
      ]);

      const summaryData = await summaryRes.json();
      const detailData = await detailRes.json();

      if (!summaryRes.ok || summaryData.success === false) {
        throw new Error(summaryData.message || "Failed to load expense report");
      }

      if (!detailRes.ok || detailData.success === false) {
        throw new Error(detailData.message || "Failed to load expense details");
      }

      setSummary(summaryData);
      setRows(summaryData.rows || []);
      setDetailRows(detailData.rows || []);
    } catch (err) {
      setError(err.message || "Expense report loading failed");
    } finally {
      setLoading(false);
    }
  }

  function viewCategory(categoryName) {
    setSelectedCategory(categoryName);
  }

  function clearCategory() {
    setSelectedCategory("ALL");
  }

  function exportDetailsCsv() {
    if (detailRows.length === 0) {
      setError("No expense detail data to export.");
      return;
    }

    const headers = [
      "Date",
      "Shop",
      "Category",
      "Type",
      "Payment",
      "Amount",
      "Reference",
      "Note",
      "Created",
    ];

    const lines = detailRows.map((row) => [
      String(row.ledger_date || "").slice(0, 10).replaceAll("-", "/"),
      row.shop_code || "",
      row.category_name || "",
      row.entry_type || "",
      row.payment_method || "",
      Number(row.amount || 0),
      row.reference_no || "",
      row.note || "",
      String(row.created_at || "").slice(0, 19).replace("T", " "),
    ]);

    const csv =
      headers.map(csvValue).join(",") +
      "\n" +
      lines.map((line) => line.map(csvValue).join(",")).join("\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `expense-report-${shop}-${selectedCategory}-${dateFrom}-to-${dateTo}.csv`;
    a.click();

    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    loadReport(selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, dateFrom, dateTo, selectedCategory]);

  const totalAmount = summary?.total?.amount || 0;
  const totalCount = summary?.total?.count || 0;
  const topCategory = rows[0];

  return (
    <Layout title="Expense Report">
      <div className="expenseReport">
        <div className="expenseReport__hero">
          <div>
            <div className="expenseReport__eyebrow">ERP Expense Analysis</div>
            <h1>Expense Report</h1>
            <p>
              View monthly or daily expense totals by category, shop and date
              range.
            </p>
          </div>

          <div className="expenseReport__filters">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />

            <LocationSelect
              value={shop}
              onChange={setShop}
              type="SHOP"
              includeAll={true}
              allLabel="All Shops"
            />

            <button onClick={() => loadReport(selectedCategory)} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {error && <div className="expenseReport__error">{error}</div>}

        <div className="expenseReport__cards">
          <div className="expenseReportCard">
            <span>Total Expense</span>
            <strong>{formatMoney(totalAmount)} Ks</strong>
            <small>Cash out in selected date range</small>
          </div>

          <div className="expenseReportCard">
            <span>Total Entries</span>
            <strong>{formatMoney(totalCount)}</strong>
            <small>Number of expense records</small>
          </div>

          <div className="expenseReportCard">
            <span>Top Category</span>
            <strong>{topCategory?.categoryName || "-"}</strong>
            <small>
              {topCategory
                ? `${formatMoney(topCategory.amount)} Ks`
                : "No expense yet"}
            </small>
          </div>

          <div className="expenseReportCard">
            <span>Detail Filter</span>
            <strong>{selectedCategory}</strong>
            <small>
              {dateFrom} to {dateTo}
            </small>
          </div>
        </div>

        <div className="expenseReportPanel">
          <div className="expenseReportPanel__head">
            <div>
              <h3>Expense Category Summary</h3>
              <p>Click View Details to filter the detail table by category.</p>
            </div>
          </div>

          <div className="expenseReportTableWrap">
            <table className="expenseReportTable">
              <thead>
                <tr>
                  <th>Expense Category</th>
                  <th>Entries</th>
                  <th>Total Amount</th>
                  <th>Percent</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="expenseReportTable__empty">
                      No expense data.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const percent =
                      totalAmount > 0
                        ? (Number(row.amount || 0) / totalAmount) * 100
                        : 0;

                    return (
                      <tr key={row.categoryName}>
                        <td>
                          <span className="expenseReportName">
                            {row.categoryName}
                          </span>
                        </td>
                        <td>{formatMoney(row.count)}</td>
                        <td>
                          <strong>{formatMoney(row.amount)} Ks</strong>
                        </td>
                        <td>
                          <div className="expensePercent">
                            <div className="expensePercent__bar">
                              <span style={{ width: `${percent}%` }} />
                            </div>
                            <b>{percent.toFixed(1)}%</b>
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="expenseSmallBtn"
                            onClick={() => viewCategory(row.categoryName)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="expenseReportPanel">
          <div className="expenseReportPanel__head expenseReportPanel__head--row">
            <div>
              <h3>Expense Detail List</h3>
              <p>
                Showing: <strong>{selectedCategory}</strong>
              </p>
            </div>

            <div className="expenseReportActions">
              <button
                type="button"
                className="expenseExportBtn"
                onClick={exportDetailsCsv}
                disabled={detailRows.length === 0}
              >
                Export CSV
              </button>

              <button
                type="button"
                className="expenseClearBtn"
                onClick={clearCategory}
                disabled={selectedCategory === "ALL"}
              >
                Show All Details
              </button>
            </div>
          </div>

          <div className="expenseReportTableWrap">
            <table className="expenseReportTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Shop</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Reference</th>
                  <th>Note</th>
                  <th>Created</th>
                </tr>
              </thead>

              <tbody>
                {detailRows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="expenseReportTable__empty">
                      No expense detail data.
                    </td>
                  </tr>
                ) : (
                  detailRows.map((row) => (
                    <tr key={row.id}>
                      <td>{String(row.ledger_date || "").slice(0, 10)}</td>
                      <td>{row.shop_code}</td>
                      <td>
                        <span className="expenseReportName">
                          {row.category_name || "-"}
                        </span>
                      </td>
                      <td>
                        <span className="expenseTypeBadge">
                          {row.entry_type || "-"}
                        </span>
                      </td>
                      <td>{row.payment_method || "-"}</td>
                      <td>
                        <strong>{formatMoney(row.amount)} Ks</strong>
                      </td>
                      <td>{row.reference_no || "-"}</td>
                      <td>{row.note || "-"}</td>
                      <td>{String(row.created_at || "").slice(0, 19)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}






